<template>
  <div class="card" :class="{ 'is-selected': isSelected }">
    <div class="header">
      <p class="title">{{ dashboard.name }}</p>
      <div class="tag-container" v-if="isCustom">
        <GenericTag
          color="black"
          background-color="var(--clr-n3)"
          isPill
          class="tag"
        >
          <span class="icon"><i class="fa-kit fa-sv-symbole"></i></span>

          <span class="title-small-regular">Conçue pour vous</span>
        </GenericTag>
      </div>
    </div>
    <div class="body">
      <div v-if="$slots.default" class="description">
        <slot />
      </div>
      <template v-if="!isCustom">
        <FilterTag
          v-for="thematicId in dashboard.filters?.thematic_ids"
          :key="thematicId"
          :text="
            (getThematicById(thematicId) as ThematicReference | undefined)?.name
          "
        />

        <FilterTag
          v-for="subthematicId in dashboard.filters?.subthematic_ids"
          :key="subthematicId"
          :text="getSubthematicById(subthematicId)?.name"
        />
        <template v-for="(filter, key) in features" :key="key">
          <FilterTag
            v-if="dashboard.filters && dashboard.filters[key]"
            :text="`${filter.name} : ${getNumberOfActiveFilters(dashboard.filters[key])}`"
          />
        </template>
        <FilterTag
          v-if="dashboard.filters?.search"
          text="🔎 Requête supplémentaire"
        />
      </template>
      <template v-else>
        <p class="typo-body-regular message">
          Cette veille a été créée par Saper Vedere. Pour la modifier, veuillez
          contacter votre interlocuteur
        </p>
      </template>
    </div>
    <div class="footer">
      <div class="actions">
        <template v-if="!isDefault && !isCustom">
          <Button
            mode="dark"
            @click="$emit('openUpdate', dashboard._id)"
            :disabled="!canEditCustomDashboard"
          >
            <template #iconLeft>
              <i class="fa-solid fa-pen" />
            </template>
            Modifier
          </Button>
          <Button
            mode="dark"
            @click="$emit('openDelete', dashboard._id)"
            :disabled="isSelected || !canEditCustomDashboard"
          >
            <template #iconLeft>
              <i class="fa-solid fa-trash" />
            </template>
            Supprimer
          </Button>
        </template>
      </div>
      <ButtonWithPaywall
        :permission-level="
          isDefault ? PermissionLevel.FREE : PermissionLevel.BASIC
        "
        :fallback-action="openDashboardPaywallModal"
        @click="$emit('apply', dashboard._id)"
        :disabled="isSelected"
      >
        Appliquer
      </ButtonWithPaywall>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from "vue";
import Button from "@follaw-fr/components/reusable/atoms/Button.vue";
import ButtonWithPaywall from "@follaw-fr/components/reusable/molecules/ButtonWithPaywall.vue";
import FilterTag from "@follaw-fr/components/reusable/atoms/FilterTag.vue";
import GenericTag from "../../reusable/atoms/GenericTag.vue";
import { features } from "@follaw-fr/utils/features";
import { openDashboardPaywallModalKey } from "@follaw-fr/constants/injection-keys";
import { PermissionLevel } from "@follaw-fr/constants/enums/permission-level.enum";
import { useUserStore } from "@/follaw-fr/stores/user";
import { useUserSettingsStore } from "@follaw-fr/stores/user-settings";
import type {
  DashboardZoomView,
  ThematicReference,
} from "@commons/types/api/schema.types";

export type DashboardCardProps = {
  dashboard: DashboardZoomView;
  isSelected?: boolean;
  isDefault?: boolean;
  isCustom?: boolean | null;
};

const {
  dashboard,
  isSelected = false,
  isDefault = false,
  isCustom,
} = defineProps<DashboardCardProps>();

defineEmits<{
  openUpdate: [id: number];
  openDelete: [id: number];
  apply: [id: number];
}>();

const openDashboardPaywallModal = inject(openDashboardPaywallModalKey);
const { hasPermission } = useUserSettingsStore();
const { getSubthematicById, getThematicById } = useUserStore();
const canEditCustomDashboard = computed(() =>
  hasPermission(PermissionLevel.BASIC)
);

const getNumberOfActiveFilters = (featureFilters: object) => {
  return Object.values(featureFilters)
    .filter((element) => element !== null)
    .reduce<number>((acc, curr) => acc + curr.length, 0);
};
</script>

<style lang="scss" scoped>
.card {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 570px;
  padding: 8px;
  border: 2px solid transparent;

  &.is-selected {
    border-color: var(--clr-p5);
  }
}

.header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-color: var(--clr-p2);
  padding-bottom: 8px;
  margin-bottom: 0;
  gap: 8px;

  .title {
    margin: 0;
    font-weight: var(--fw-semibold);
    line-height: var(--lh-normal);
    flex: 1;
  }

  .tag-container {
    display: flex;
    align-self: flex-start;
  }
  .tag {
    display: flex;
    align-items: center;
    padding: 4px 16px;
    gap: 8px;

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 12px;
      height: 8px;
    }
  }
}

.body {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;

  .message {
    font-style: italic;
  }
}

.footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;

  .actions {
    display: flex;
    gap: 8px;

    button {
      display: flex;
      gap: 8px;

      .fa-pen {
        color: var(--clr-s7);
      }

      .fa-trash {
        color: var(--clr-e300);
      }
    }
  }
}
</style>
